"use client";

import React from "react";
import { ArrowRight, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const phases = [{
  id: "01",
  title: "Discovery & Diagnosis",
  description: "We begin by deeply understanding your institution's unique mission, market position, and internal culture. We don't just audit; we align with your reality to identify true opportunities for lifelong learning growth."
}, {
  id: "02",
  title: "Strategic Co-Design",
  description: "Unlike traditional firms that deliver a strategy *to* you, we build it *with* you. We collaborate with your internal stakeholders to design a pragmatic roadmap that your team understands, believes in, and can actually execute."
}, {
  id: "03",
  title: "Capability Building",
  description: "This is where we differ. We don't just hand over a PDF and leave. We embed the necessary skills, systems, and processes into your organization, training your team to manage the new ecosystem effectively."
}, {
  id: "  04",
  title: "Ownership",
  description: "The ultimate goal. Your team has the internal capacity to sustain, adapt, and grow the ecosystem independently. We step back, leaving you with sustainable growth you truly own.",
  isFinal: true
}];
export default function TheCoDesignProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-co-design-process" ref={ref} className="bg-background relative overflow-hidden py-24 sm:py-32">
      <div className="container mx-auto px-4 md:px-6">
        {}
        <div className={`mb-16 max-w-3xl transition-all duration-700 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <h2 className="font-serif text-foreground text-4xl font-medium tracking-tight sm:text-5xl">
            The Co-Design Process
          </h2>
          <p className="text-muted-foreground mt-6 text-lg leading-relaxed">
            We don't just deliver a strategy; we build your capacity to execute it.
            Our four-phase approach ensures that by the time we leave, the growth is yours to keep.
          </p>
        </div>

        {}
        <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          {}
          <div className="bg-secondary/20 absolute top-16 left-0 h-1 w-full -translate-y-1/2 rounded-full md:top-20" />
          
          {}
          <div className="bg-secondary absolute top-16 left-0 h-1 w-1/4 -translate-y-1/2 rounded-full opacity-50 md:top-20" />

          <ScrollArea className="w-full whitespace-nowrap pb-8">
            <div className="flex w-max space-x-6 p-1 md:space-x-12 md:p-4">
              {phases.map((phase, i) => <div key={phase.id} data-index={i} className="relative pt-4">
                  <Card className={`group relative h-full w-[85vw] border-none shadow-none transition-all duration-500 sm:w-[400px] ${phase.isFinal ? "bg-background shadow-[0_0_40px_-10px_var(--color-chart-3)] ring-1 ring-chart-3/50" : "bg-transparent"}`}>
                    <div className="relative z-10 flex flex-col gap-6">
                      {}
                      <div className="flex items-center gap-4">
                        <span className="font-serif text-7xl leading-none tracking-tighter transition-colors duration-300 drop-shadow-sm ![margin:0px_5px_0px_5px] !font-semibold !text-[hsl(44,84%,48%)] md:text-8xl">
                          {phase.id}
                        </span>
                        {phase.isFinal && <div className="bg-chart-3/10 text-chart-3 flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider">
                            <CircleCheck className="h-4 w-4" />
                            Goal Achieved
                          </div>}
                      </div>

                      {}
                      <div className="space-y-4 whitespace-normal pr-4">
                        <h3 className="font-serif text-2xl font-medium text-foreground ![margin:0px_15px_16px_0px] !w-[400px] !text-center">
                          {phase.title}
                        </h3>
                        <p className="text-muted-foreground text-base leading-relaxed !w-[350px] ![margin:0px_10px_0px_10px]">
                          {phase.description}
                        </p>
                      </div>
                    </div>

                    {}
                    <div className={`absolute top-12 -left-3 z-0 h-6 w-6 -translate-y-1/2 rounded-full border-4 bg-background transition-colors duration-300 md:top-16 md:-left-6 ${phase.isFinal ? "border-chart-3 shadow-[0_0_15px_var(--color-chart-3)]" : "border-secondary group-hover:bg-secondary/20"}`} />
                  </Card>
                </div>)}
              
              {}
              <div className="flex w-[85vw] flex-col items-center justify-center px-4 text-center sm:w-[300px]">
                <div className="space-y-6">
                  <h3 className="font-serif text-3xl text-foreground">
                    Ready to start?
                  </h3>
                  <p className="text-muted-foreground !w-[350px]">Let's discuss how we can co-design your 

institution's growth.</p>
                  <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto">
                    <Link to="https://edustrategygroup.com/schedule-your-strategy-session/">
                      Start a Conversation <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      </div>
    </section>;
}